import { TFunction } from "i18next";
import GuestBreakdown from "@/model/GuestBreakdown";

/** Same limits as the API's GuestBreakdown schema. */
const MAX_GUESTS = 50;
const MAX_INFANTS = 5;

const ONE_ADULT: GuestBreakdown = { adults: 1, children: 0, infants: 0 };

function count(value: string | null): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

/** Adults + children: what capacity, the search filter and per-guest extras use. */
function countedGuests(guests: GuestBreakdown): number {
  return guests.adults + guests.children;
}

/**
 * Reads adults/children/infants from the URL. Links from before the breakdown only have
 * `guests`, read as that many adults. Null when the URL says nothing about guests.
 */
function fromSearchParams(params: URLSearchParams): GuestBreakdown | null {
  const legacyGuests = count(params.get("guests"));
  const adults = count(params.get("adults")) || legacyGuests;
  const children = count(params.get("children"));
  const infants = Math.min(count(params.get("infants")), MAX_INFANTS);

  if (adults === 0 && children === 0 && infants === 0) {
    return null;
  }
  // A stay always has an adult, like the picker enforces.
  return { adults: Math.max(adults, 1), children, infants };
}

function setSearchParams(params: URLSearchParams, guests: GuestBreakdown | null) {
  params.delete("guests");
  params.delete("adults");
  params.delete("children");
  params.delete("infants");
  if (!guests) {
    return;
  }
  params.set("adults", String(guests.adults));
  if (guests.children > 0) {
    params.set("children", String(guests.children));
  }
  if (guests.infants > 0) {
    params.set("infants", String(guests.infants));
  }
}

/** "3 guests, 1 infant": the short summary shown on the picker button. */
function summarize(t: TFunction, guests: GuestBreakdown): string {
  return [
    t("guests.summary", { count: countedGuests(guests) }),
    guests.infants > 0 ? t("guests.infantCount", { count: guests.infants }) : "",
  ]
    .filter(Boolean)
    .join(", ");
}

/** "2 adults, 1 child, 1 infant": the detailed breakdown, leaving out empty categories. */
function describe(t: TFunction, guests: GuestBreakdown): string {
  return [
    guests.adults > 0 ? t("guests.adultCount", { count: guests.adults }) : "",
    guests.children > 0 ? t("guests.childCount", { count: guests.children }) : "",
    guests.infants > 0 ? t("guests.infantCount", { count: guests.infants }) : "",
  ]
    .filter(Boolean)
    .join(", ");
}

/** Bookings made before the breakdown existed only have guestCount; they're shown as adults. */
function ofBooking(booking: {
  guests?: GuestBreakdown;
  guestCount?: number;
}): GuestBreakdown {
  return booking.guests ?? { adults: booking.guestCount ?? 1, children: 0, infants: 0 };
}

const guestsUtils = {
  MAX_GUESTS,
  MAX_INFANTS,
  ONE_ADULT,
  countedGuests,
  fromSearchParams,
  setSearchParams,
  summarize,
  describe,
  ofBooking,
};

export default guestsUtils;
